import React, { ReactElement } from 'react';
import TitleContainer from './components/TitleContainer';
import { useTranslation } from 'react-i18next';
import styles from './components/styles/Activity.module.scss';
import { Card } from 'semantic-ui-react';
import { useState, useEffect } from 'react';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { useTitleDispatch } from '@components/TitleContextProvider';
import useAllActions from '@behavior/activity/getAllActions';
import { Loading } from 'react-lib/dist/components-es';
import { Table } from 'semantic-ui-react';
import { TableHeader } from 'semantic-ui-react';
import { TableRow } from 'semantic-ui-react';
import { TableHeaderCell } from 'semantic-ui-react';
import { TableBody } from 'semantic-ui-react';
import { TableCell } from 'semantic-ui-react';
import { actionsKey } from '@src/constants';
import { Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import ButtonWrapper from '@components/common/ButtonWrapper';
import useMarkActionAsCompleted from '@behavior/activity/useMarkActionAsCompleted';
import DateFormattedCell from '@components/Filtering/OverviewTable/CeresCustomOverviewTableComponents/DateFormattedCell';
import ErrorPage from '@src/pages/ErrorPage';

const ActionsDetailsContainer = () => {
  const { t } = useTranslation();
  const dispatch = useTitleDispatch();
  const { actions, headerRow, isLoading } = useAllActions();
  const useMarkActionAsCompletedMutate = useMarkActionAsCompleted();

  useEffect(() => {
    const pageTitle = t('page.all.actions.title');
    dispatch({
      pageTitle,
    });
  }, dispatch);

  if (isLoading) {
    return <Loading isLoading />;
  }

  if (!actions || !headerRow) {
    return <ErrorPage />;
  }

  const getActionLink = (action: ActionDTO) => {
    return (
      <Link to={`/actions/${action.id}`}>
        <Icon name="linkify" />
      </Link>
    );
  };

  const handleMarkAsCompleted = (action: ActionDTO) => {
    if (action.id) {
      useMarkActionAsCompletedMutate(action.id);
    }
  };

  const getCompletionStatusContent = (action: ActionDTO) => {
    return action.completionDte ? (
      <h4>{t('message.action.completed')}</h4>
    ) : (
      <ButtonWrapper
        label="action.mark.as.completed"
        primary
        icon="checkmark"
        onClick={() => handleMarkAsCompleted(action)}
      />
    );
  };

  const getDateContent = (action: ActionDTO, key: string, showTime = true) => {
    const entry = {
      key,
    };
    return (
      <DateFormattedCell rowValues={action} entryRecord={entry} disableBorder showTime={showTime} />
    );
  };

  const getCompletionDteContent = (action: ActionDTO) => {
    if (action.completionDte) {
      return getDateContent(action, 'completionDte');
    } else {
      return t('action.not.yet.completed');
    }
  };

  const renderTableRow = (action: ActionDTO, headerRow: string[]) => {
    const tableRow = headerRow.map(title => {
      let content: string | ReactElement = '';

      switch (title) {
        case actionsKey.ACTION_COMPLETIONDTE:
          content = getCompletionDteContent(action);
          break;
        case actionsKey.ACTION_DESCRIPTION:
          content = action.description || '';
          break;
        case actionsKey.ACTION_RESOLVER:
          content = action.assignedUser?.name || '';
          break;
        case actionsKey.ACTION_CREATEDTE:
          content = getDateContent(action, 'createDte');
          break;
        case actionsKey.ACTION_LINK:
          content = getActionLink(action);
          break;
        case actionsKey.ACTION_COMPLETION_STATUS:
          content = getCompletionStatusContent(action);
          break;
        case actionsKey.ACTION_DEADLINE_DATE:
          content = getDateContent(action, 'deadlineDte', false);
          break;
        default:
          break;
      }

      return (
        <TableCell
          key={title}
          className={title === actionsKey.ACTION_DESCRIPTION ? 'actionDetailDescription' : ''}
        >
          {content}
        </TableCell>
      );
    });

    let isPositive: boolean = false;
    if (action.completionDte) {
      isPositive = true;
    }

    return (
      <TableRow key={action.id} positive={isPositive} negative={!isPositive}>
        {tableRow}
      </TableRow>
    );
  };

  const renderTableBody = (actions: ActionDTO[], headerRow: string[]) => {
    return <TableBody>{actions.map(action => renderTableRow(action, headerRow))}</TableBody>;
  };

  return (
    <div className={styles.component}>
      <Card className={styles.card}>
        <TitleContainer header="actions.details.page.action.details.container.title" />
        <Table celled>
          <TableHeader>
            <TableRow>
              {headerRow?.map((title: string) => (
                <TableHeaderCell key={`${title}_key`}>{t(title)}</TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          {actions && headerRow && renderTableBody(actions, headerRow)}
        </Table>
      </Card>
    </div>
  );
};

export default ActionsDetailsContainer;
